import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Map,
  Package,
  ImageIcon,
  Video,
  Settings,
  ChevronLeft,
} from 'lucide-react';
import { cn } from '@/lib/utils';

export interface SidebarProps {
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}

interface NavItem {
  label: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
}

const navItems: NavItem[] = [
  {
    label: 'Dashboard',
    href: '/',
    icon: LayoutDashboard,
  },
  {
    label: 'Trails',
    href: '/trails',
    icon: Map,
  },
  {
    label: 'Packages',
    href: '/packages',
    icon: Package,
  },
  {
    label: 'Gallery',
    href: '/gallery',
    icon: ImageIcon,
  },
  {
    label: 'Media Pipeline',
    href: '/media-pipeline',
    icon: Video,
  },
  {
    label: 'Settings',
    href: '/settings',
    icon: Settings,
  },
];

export default function Sidebar({ isCollapsed, onToggleCollapse }: SidebarProps) {
  return (
    <aside
      className={cn(
        'flex h-screen flex-col border-r border-neutral-200 bg-white transition-all duration-300',
        isCollapsed ? 'w-16' : 'w-64'
      )}
    >
      {/* Logo / Brand */}
      <div className="flex h-16 items-center justify-between border-b border-neutral-200 px-4">
        {!isCollapsed && (
          <div className="flex-1">
            <h1 className="text-lg font-bold text-primary-600">Laurentian</h1>
            <p className="text-xs text-neutral-600">Content Management</p>
          </div>
        )}
        <button
          onClick={onToggleCollapse}
          className="rounded-md p-1.5 hover:bg-neutral-100"
          title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <ChevronLeft
            className={cn(
              'h-5 w-5 text-neutral-600 transition-transform',
              isCollapsed && 'rotate-180'
            )}
          />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 space-y-1 p-2">
        {navItems.map((item) => (
          <NavLink
            key={item.href}
            to={item.href}
            end={item.href === '/'}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                isActive
                  ? 'bg-primary-50 text-primary-700'
                  : 'text-neutral-700 hover:bg-neutral-100',
                isCollapsed && 'justify-center'
              )
            }
            title={isCollapsed ? item.label : undefined}
          >
            <item.icon className="h-5 w-5 flex-shrink-0" />
            {!isCollapsed && <span>{item.label}</span>}
          </NavLink>
        ))}
      </nav>

      {/* Footer */}
      <div className="border-t border-neutral-200 p-4">
        {!isCollapsed && (
          <p className="text-xs text-neutral-500">
            © {new Date().getFullYear()} Laurentian Adventures
          </p>
        )}
      </div>
    </aside>
  );
}
